import { existsSync, readFileSync } from "node:fs";
import { join, resolve } from "node:path";
import { Command } from "commander";
import { logErr, logInfo, S } from "../helpers.js";
import { getStoreConfig, storeFetch } from "../store-client.js";

/**
 * Creates the `skaile verify` command.
 *
 * Checks installed assets in `skaile.lock.yaml` against the store's signed
 * manifests. Exits 1 on hash mismatches; with `--strict` also exits 1 if any
 * asset has no store record.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#verify
 */
export function makeVerifyCommand(): Command {
  return new Command("verify")
    .description("Verify installed assets against store signatures")
    .option("--strict", "Exit with code 1 if any asset is unverified")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const projectDir = resolve(opts.projectDir);
      const lockPath = join(projectDir, "skaile.lock.yaml");

      if (!existsSync(lockPath)) {
        logInfo("No skaile.lock.yaml found — nothing to verify");
        return;
      }

      // Parse lock file
      const yaml = await import("yaml");
      const lockContent = readFileSync(lockPath, "utf-8");
      const lock = yaml.parse(lockContent);

      if (!lock?.assets || Object.keys(lock.assets).length === 0) {
        logInfo("No assets in lock file");
        return;
      }

      console.log();
      console.log(`  ${S.heading("Verifying installed assets...")}`);
      console.log();

      let verified = 0;
      let unverified = 0;
      let failed = 0;

      const config = getStoreConfig();

      for (const [ref, entry] of Object.entries(lock.assets) as [string, any][]) {
        if (!entry.store_ref || !entry.content_hash || !entry.store_signature) {
          // No store metadata — local-only asset
          console.log(`  ${S.dim("○")} ${ref.padEnd(40)} ${S.dim("local only — no store record")}`);
          unverified++;
          continue;
        }

        try {
          // Extract publisher/slug/version from store_ref URL
          // Expected path: /api/assets/:publisher/:slug/:version
          const urlParts = new URL(entry.store_ref).pathname.split("/").filter(Boolean);
          const [, , publisher, slug, version] = urlParts;

          const sigData = await storeFetch<{ manifest: any; signature: string }>(
            config,
            `/api/assets/${publisher}/${slug}/${version}/sig`,
          );

          // Compare content hashes
          if (sigData.manifest.content_hash === entry.content_hash) {
            console.log(
              `  ${S.ok("✓")} ${ref.padEnd(40)} ${S.dim(`${entry.content_hash.slice(0, 20)}...`)} ${S.ok("verified")}`,
            );
            verified++;
          } else {
            console.log(
              `  ${S.err("✗")} ${ref.padEnd(40)} ${S.err("HASH MISMATCH")} — expected ${entry.content_hash.slice(0, 16)}... got ${sigData.manifest.content_hash.slice(0, 16)}...`,
            );
            failed++;
          }
        } catch (e: any) {
          console.log(
            `  ${S.warn("!")} ${ref.padEnd(40)} ${S.warn(`verification failed: ${e.message}`)}`,
          );
          failed++;
        }
      }

      console.log();
      console.log(
        `  ${S.heading("Summary:")} ${S.ok(`${verified} verified`)}  ${unverified ? S.dim(`${unverified} unverified`) : ""}  ${failed ? S.err(`${failed} failed`) : ""}`,
      );
      console.log();

      if (opts.strict && (unverified > 0 || failed > 0)) {
        logErr("Strict mode: not all assets are verified");
        process.exit(1);
      }

      if (failed > 0) {
        process.exit(1);
      }
    });
}
